import { LogOut } from "lucide-react"
import {
  ChangeEventHandler,
  SubmitEventHandler,
  useEffect,
  useState,
} from "react"
import { Button } from "shared-web/components/base/control/Button"
import { TextInput } from "shared-web/components/base/input/text/TextInput"
import { ModalShell } from "shared-web/components/base/overlay/Modal"
import { toast } from "shared-web/lib/toast"

import { useAppDispatch } from "app/hooks"
import {
  companyApiSlice,
  useGetCurrentUserIsAttributionEnabledQuery,
  useGetCurrentUserQuery,
  useValidateStaffAttributionPinMutation,
} from "features/api/company"

import { useSignOut } from "./useSignOut"

export const PIN_CHANGED_LS_KEY = "rundoo_pinchanged"

export function PINModal() {
  const { currentData: userData } = useGetCurrentUserQuery({})
  const hasAttributionStaff = userData?.user?.attributionStaff !== undefined

  const { currentData: isAttributionEnabledData } =
    useGetCurrentUserIsAttributionEnabledQuery({})
  const isAttributionEnabled =
    isAttributionEnabledData?.isAttributionEnabled ?? false

  // Cross-tab sync: localStorage "storage" events notify other tabs to
  // refetch the current user when the PIN cookie is set or cleared.
  const dispatch = useAppDispatch()
  useEffect(() => {
    function handler(e: StorageEvent) {
      if (e.key !== PIN_CHANGED_LS_KEY) return
      dispatch(companyApiSlice.util.invalidateTags(["CurrentUser"]))
    }
    window.addEventListener("storage", handler)
    return () => {
      window.removeEventListener("storage", handler)
    }
  }, [dispatch])

  if (!isAttributionEnabled || hasAttributionStaff) {
    return null
  }

  return (
    <ModalShell size="xs" panelClassName="max-w-94 p-0" onClose={() => {}} open>
      <PINModalContent />
    </ModalShell>
  )
}

function PINModalContent() {
  const [pin, setPIN] = useState<string>("")

  const [validateStaffAttributionPIN, { isLoading: isValidating }] =
    useValidateStaffAttributionPinMutation()
  const { signOut, isSigningOut } = useSignOut()

  const onChangePIN: ChangeEventHandler<HTMLInputElement> = (e) => {
    const val = e.target.value
    if (val !== "" && Number.isNaN(Number(val))) return
    setPIN(val)
  }

  const onSubmit: SubmitEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()

    try {
      await validateStaffAttributionPIN({ pin }).unwrap()
      localStorage.setItem(PIN_CHANGED_LS_KEY, Date.now().toString())
    } catch (error) {
      toast.error({ message: "Something went wrong. Please try again.", error })
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col">
      <h1 className="color-default text-title-md border-v2-border-default w-full border-b px-6 py-5 text-left">
        Enter your PIN
      </h1>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-col gap-4">
          <TextInput
            value={pin}
            onChange={onChangePIN}
            maxLength={6}
            type="password"
            autoComplete="off"
            inputMode="numeric"
            autoFocus
            data-autofocus
            data-1p-ignore
          />
          <Button
            mode="primary"
            type="submit"
            loading={isValidating}
            disabled={pin.length < 2}
          >
            Submit
          </Button>
        </div>
        <div className="border-v2-border-default flex flex-col border-t pt-6">
          <Button
            mode="secondary"
            leftIcon={LogOut}
            onClick={() => signOut()}
            loading={isSigningOut}
          >
            Sign out
          </Button>
        </div>
      </div>
    </form>
  )
}
